import { KEY_HANDLERS, LAYOUTSTATE, ROUTESTATE, TransitionTypes } from '.'
import { KeyEvent, ViewMode } from '../constants/enums'
import { FLOATING_DEBUG } from '../constants/floaters'
import { motionTransition } from '../utilities/transitions'
import { getModalState } from './getState'
import { generateModalKey, setShowMultiModal } from './internal/modals'
import { setRoomViewMode } from './internal/rooms'
import { closeTopFloater } from './modals/state.floater'
import { openFloatingDebug } from './state.debug'

const debugHotkey = (event: KeyboardEvent) => {
    if (event.altKey && event.shiftKey) {
        const debugKey = generateModalKey(FLOATING_DEBUG)
        if (getModalState().floater[debugKey]?.show)
            setShowMultiModal(debugKey, false)
        else openFloatingDebug()
    }
}

// one thing a press, from the top: a full window covers the floaters, which
// float over a room in any other view
export const onEscape = () => {
    if (closeTopFloater()) return
    const floatingRoom = getModalState().floating_room
    // a minimized room isn't on screen: its mini player closes as a floater
    const roomOnScreen = floatingRoom.show && !floatingRoom.minimized
    if (!roomOnScreen || !floatingRoom.id) return
    if (floatingRoom.viewMode === ViewMode.Normal) return
    motionTransition(
        () =>
            setRoomViewMode(
                ViewMode.Normal,
                floatingRoom.viewMode === ViewMode.FullWindow,
            ),
        {
            id: floatingRoom.id,
            transitionSource: TransitionTypes.ROOM,
            transitionDestination: ROUTESTATE.lastTransitionSource,
        },
    )
}

const GLOBAL_HANDLERS = {
    [KeyEvent.Keydown]: {
        Escape: onEscape,
        '`': debugHotkey, //osx/ios trigger on this
        '~': debugHotkey, //win/linux trigger on this
    },
    // [KeyEvent.Keypress]: {

    // },
    // [KeyEvent.Keyup]: {

    // }
}

// an empty portal composer has no caret to move or text to cancel
const COMPOSER_PASSTHROUGH_KEYS = ['ArrowLeft', 'ArrowRight', 'Escape']

const passesThroughComposer = ({ key, target }: KeyboardEvent) =>
    COMPOSER_PASSTHROUGH_KEYS.includes(key) &&
    target instanceof HTMLTextAreaElement &&
    'portalComposer' in target.dataset &&
    target.value === ''

if (typeof window !== 'undefined') {
    // window.__MFC_APP_KEYS__ =
    const mainHandler = (eventType: KeyEvent) => (event: KeyboardEvent) => {
        // console.log(
        //     '[keys mainHandler]',
        //     {
        //         eventType,
        //         key: event.key,
        //         composeFocused: LAYOUTSTATE.portal.composeFocused,
        //     },
        //     KEY_HANDLERS[eventType],
        // )
        // typing keeps its keys from all but the handlers that ask for them
        const typing =
            LAYOUTSTATE.portal.composeFocused && !passesThroughComposer(event)
        const handlers = KEY_HANDLERS[eventType]
        let foundExclusive = false
        handlers.forEach((handler) => {
            if (typing && !handler.whileTyping) return
            if (handler.key.includes(event.key)) {
                if (foundExclusive) return
                handler.callback(event.key, event)
                if (handler.exclusive) {
                    foundExclusive = true
                }
            }
        })
        if (foundExclusive || typing) return
        // global handlers
        const globalHandler = GLOBAL_HANDLERS[eventType][event.key]
        globalHandler?.(event)
    }
    window.document.addEventListener(
        KeyEvent.Keydown,
        mainHandler(KeyEvent.Keydown),
        { passive: true },
    )
    // window.document.addEventListener(
    //     KeyEvent.Keyup,
    //     mainHandler(KeyEvent.Keyup),
    //     { passive: true },
    // )
    // window.document.addEventListener(
    //     KeyEvent.Keypress,
    //     mainHandler(KeyEvent.Keypress),
    //     { passive: true },
    // )
}

export const registerKeyHandler = (
    eventType: KeyEvent = KeyEvent.Keydown,
    handler: (key: string, event: KeyboardEvent) => any,
    key: string[],
    options?: {
        /*modKeys?: string[];*/
        exclusive?: boolean
        whileTyping?: boolean
    },
) => {
    const { exclusive = false, whileTyping = false } = options || {}
    const existing = KEY_HANDLERS[eventType]
    const keyHandler = { key, exclusive, whileTyping, callback: handler }
    // if its `exclusive`, put it first in the array
    if (exclusive) {
        KEY_HANDLERS[eventType] = [keyHandler].concat(existing)
    } else {
        KEY_HANDLERS[eventType] = existing.concat(keyHandler)
    }
    return () => {
        //deregister
        const index = KEY_HANDLERS[eventType].findIndex(
            (h) => h.callback === handler,
        )
        if (index !== -1) KEY_HANDLERS[eventType].splice(index, 1)
    }
}
